/**
 * 내 여행으로 담기 — 어느 여행에 담을지 고르는 시트.
 *
 * 왜 고르게 하나
 *   여행은 여러 개일 수 있고, 잘못 담으면 남의 일정 7곳이 내 여행에
 *   섞입니다. 되돌리려면 한 곳씩 지워야 해서 묻는 쪽이 낫습니다.
 *
 * 각 줄에 여행 일수와 이미 담긴 장소 수를 보여 주는 이유
 *   3일 코스를 2일 여행에 담으면 Day 3 이 Day 2 로 접힙니다. 담기 전에
 *   그 사실을 알 수 있어야 합니다. 이미 담긴 장소 수는 "빈 여행인지"
 *   를 알려 줍니다 — 빈 여행에 담는 게 가장 깔끔합니다.
 *
 * 목록은 시트를 열 때 받습니다. 코스 상세를 여는 사람 대부분은 구경만
 * 하고 나가서, 그 몫까지 내 여행 목록을 조회할 이유가 없습니다.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Sheet } from 'components/ds';
import { IcPin } from 'components/common/icons/EmojiIcons';
import { CourseCopyTarget } from 'hooks/useTravelCourse';
import styles from './CourseCopySheet.module.css';

interface Props {
    open: boolean;
    onClose: () => void;
    /** 담을 코스의 일수 — 여행이 더 짧으면 접힘을 미리 알립니다 */
    courseDays?: number | null;
    targets: CourseCopyTarget[];
    loading: boolean;
    loaded: boolean;
    busy: boolean;
    onLoad: () => void;
    onPick: (meetingId: string) => void;
}

const fmtRange = (from?: string | null, to?: string | null): string => {
    const f = from ? new Date(from) : null;
    const t2 = to ? new Date(to) : null;
    if (!f || Number.isNaN(f.getTime())) return '';
    const one = (d: Date) => `${d.getMonth() + 1}.${d.getDate()}`;
    if (!t2 || Number.isNaN(t2.getTime())) return one(f);
    return `${one(f)} – ${one(t2)}`;
};

export const CourseCopySheet = ({
    open, onClose, courseDays, targets, loading, loaded, busy, onLoad, onPick,
}: Props) => {
    const { t } = useTranslation();

    useEffect(() => {
        if (open && !loaded) onLoad();
    }, [open, loaded, onLoad]);

    return (
        <Sheet
            open={open}
            onClose={onClose}
            title={t('course.copyToMine')}
            closeLabel={t('common.close')}
        >
            {loading && <p className={styles.state}>{t('common.loading')}</p>}

            {/* 담을 여행이 없는 경우 — 버튼을 잠그기보다 왜 없는지 말합니다 */}
            {!loading && loaded && targets.length === 0 && (
                <p className={styles.state}>{t('course.noTarget')}</p>
            )}

            {targets.length > 0 && (
                <ul className={styles.list}>
                    {targets.map((m) => {
                        const short = !!(courseDays && m.days && courseDays > m.days);
                        return (
                            <li key={m.meetingId}>
                                <button
                                    type="button"
                                    className={styles.row}
                                    disabled={busy || !!m.alreadyCopied}
                                    onClick={() => onPick(m.meetingId)}
                                >
                                    <span className={styles.rowBody}>
                                        <span className={styles.name}>{m.meetingName}</span>
                                        <span className={styles.meta}>
                                            {m.travelDestination && (
                                                <>
                                                    <IcPin size={11} />
                                                    {m.travelDestination}
                                                </>
                                            )}
                                            {fmtRange(m.travelStartDate, m.travelEndDate) && (
                                                <span>{fmtRange(m.travelStartDate, m.travelEndDate)}</span>
                                            )}
                                            <span>
                                                {t('course.targetDays', { n: m.days || 1 })}
                                            </span>
                                            {(m.placeCount || 0) > 0 && (
                                                <span>
                                                    {t('course.targetHas', { n: m.placeCount })}
                                                </span>
                                            )}
                                        </span>
                                        {/* 접힘은 담고 나서 알면 늦습니다 */}
                                        {short && (
                                            <span className={styles.warn}>
                                                {t('course.willFold', { n: m.days })}
                                            </span>
                                        )}
                                    </span>
                                    <span className={styles.pick}>
                                        {m.alreadyCopied ? t('course.already') : t('course.pick')}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            <div className={styles.footer}>
                <Button variant="ghost" size="lg" block onClick={onClose}>
                    {t('common.close')}
                </Button>
            </div>
        </Sheet>
    );
};
